import { useMemo } from 'react';
import { SEGMENTS } from '../../data/conjointData';
import { getRespondentsBySegment, computeDemographicProfile } from '../../lib/segmentAssignment';
import type { KMeansSegment } from '../../lib/segmentAssignment';
import { formatPercent, formatPrice } from '../../lib/formatters';
import { InfoButton } from '../common/Tooltip';
import { Users, Target, ShoppingBag, Smartphone, BarChart3 } from 'lucide-react';

const SEGMENT_ORDER: KMeansSegment[] = ['Price-Sensitive Buyers', 'Low-Interest Prospects', 'Budget Seekers'];

export function SegmentDemographicProfiles() {
  const bySegment = useMemo(() => getRespondentsBySegment(), []);
  const profiles = useMemo(() => {
    const result: Record<string, ReturnType<typeof computeDemographicProfile>> = {};
    for (const seg of SEGMENT_ORDER) {
      result[seg] = computeDemographicProfile(bySegment[seg]);
    }
    return result;
  }, [bySegment]);

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-1">
        <Users size={16} className="text-chart-blue" />
        <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">Segment Demographic Profiles</h3>
        <InfoButton helpKey="segmentDemographics" size={13} />
      </div>
      <p className="text-sm text-muted mb-4">
        Respondents assigned to segments based on behavioral similarity (none rate and price distribution). Demographics, attitudes, and preferences for each group.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {SEGMENT_ORDER.map(segName => {
          const seg = SEGMENTS.find(s => s.name === segName);
          const profile = profiles[segName];
          if (!seg || !profile) return null;

          return (
            <div key={segName} className="rounded-xl overflow-hidden border" style={{ borderColor: `${seg.color}30` }}>
              {/* Header */}
              <div className="px-4 py-3" style={{ background: `linear-gradient(135deg, ${seg.color}10 0%, ${seg.color}06 100%)` }}>
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: seg.color }} />
                  <h4 className="text-sm font-bold text-primary">{seg.shortName}</h4>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  <KeyStat label="Respondents" value={String(profile.count)} />
                  <KeyStat label="Avg Age" value={String(profile.avgAge)} />
                  <KeyStat label="None Rate" value={formatPercent(profile.avgNoneRate)} />
                </div>
              </div>

              <div className="px-4 py-3 space-y-0">
                {/* Demographics */}
                <ProfileSection title="Demographics" icon={<Users size={11} />}>
                  <DemoTable rows={[
                    { label: 'Gender', items: profile.gender },
                    { label: 'Age', items: [
                      { label: '18-34', pct: profile.ageBands['18-34'], count: 0 },
                      { label: '35-49', pct: profile.ageBands['35-49'], count: 0 },
                      { label: '50+', pct: profile.ageBands['50+'], count: 0 },
                    ]},
                    { label: 'Income', items: profile.income.slice(0, 3) },
                    ...(profile.region.length > 0 ? [{ label: 'Region', items: profile.region.slice(0, 3) }] : []),
                    ...(profile.employment.length > 0 ? [{ label: 'Work', items: profile.employment.slice(0, 2) }] : []),
                  ]} segColor={seg.color} />
                </ProfileSection>

                {/* Attitudes */}
                <ProfileSection title="Attitudes" icon={<Target size={11} />}>
                  <div className="space-y-1.5">
                    {profile.attitudes.map(a => (
                      <AttitudeBar
                        key={a.statement}
                        label={shortenStatement(a.statement)}
                        value={a.agreeRate}
                        segColor={seg.color}
                      />
                    ))}
                  </div>
                </ProfileSection>

                {/* Purchase Factors */}
                <ProfileSection title="Purchase Factors" icon={<ShoppingBag size={11} />}>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                    {profile.purchaseFactors.slice(0, 4).map(f => (
                      <div key={f.factor} className="flex items-center justify-between">
                        <span className="text-micro text-secondary truncate pr-1">{f.factor}</span>
                        <span className="font-mono text-micro text-primary font-medium tabular-nums">#{f.avgRank}</span>
                      </div>
                    ))}
                  </div>
                </ProfileSection>

                {/* Brands & Devices */}
                <ProfileSection title="Brands & Devices" icon={<Smartphone size={11} />}>
                  <div className="space-y-2">
                    <div>
                      <div className="text-micro text-muted uppercase tracking-wider mb-1">Top Brands</div>
                      <div className="flex flex-wrap gap-1">
                        {profile.brands.filter(b => b.label !== 'None of these').slice(0, 4).map(b => (
                          <span key={b.label} className="text-micro px-2 py-0.5 rounded-md text-secondary" style={{ backgroundColor: 'var(--color-surface)' }}>
                            {b.label} <span className="font-mono text-muted">{b.pct}%</span>
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-micro text-muted uppercase tracking-wider mb-1">Wearables</div>
                      <div className="flex flex-wrap gap-1">
                        {profile.devices.filter(d => d.label !== 'None of the above').slice(0, 4).map(d => (
                          <span key={d.label} className="text-micro px-2 py-0.5 rounded-md text-secondary" style={{ backgroundColor: 'var(--color-surface)' }}>
                            {d.label} <span className="font-mono text-muted">{d.pct}%</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </ProfileSection>

                {/* Feature Preferences */}
                <ProfileSection title="Top Features" icon={<BarChart3 size={11} />}>
                  <div className="space-y-1">
                    {profile.topFeatures.slice(0, 3).map((f, i) => (
                      <div key={f.feature} className="flex items-center gap-2">
                        <span className="text-micro font-mono font-semibold text-muted w-4 shrink-0">{i + 1}.</span>
                        <span className="text-micro text-secondary flex-1 truncate">{f.feature}</span>
                        <span className="font-mono text-micro text-muted tabular-nums">#{f.avgRank}</span>
                      </div>
                    ))}
                  </div>
                </ProfileSection>

                {/* Goals & Spend */}
                <ProfileSection title="Goals & Spend" icon={<Target size={11} />}>
                  <DemoTable rows={[
                    { label: 'Goal', items: profile.primaryGoal.slice(0, 2) },
                    { label: 'Spend', items: profile.monthlySpend.slice(0, 3) },
                  ]} segColor={seg.color} />
                  {profile.avgChosenPrice && (
                    <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-border/20">
                      <span className="text-micro text-muted">Avg chosen price</span>
                      <span className="font-mono text-micro text-primary font-semibold">{formatPrice(profile.avgChosenPrice)}</span>
                    </div>
                  )}
                </ProfileSection>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Sub-components ── */

function KeyStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-center">
      <div className="font-mono text-xs font-semibold text-primary tabular-nums">{value}</div>
      <div className="text-micro text-muted">{label}</div>
    </div>
  );
}

function ProfileSection({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="py-2.5 border-t border-border/30">
      <div className="flex items-center gap-1.5 mb-2">
        <span className="text-muted">{icon}</span>
        <span className="text-micro text-muted uppercase tracking-wider font-semibold">{title}</span>
      </div>
      {children}
    </div>
  );
}

/** Tabular demographic breakdown — label left, items as horizontal bar-like rows */
function DemoTable({ rows, segColor }: { rows: { label: string; items: { label: string; pct: number; count: number }[] }[]; segColor: string }) {
  return (
    <div className="space-y-2">
      {rows.map(row => (
        <div key={row.label}>
          <div className="text-micro text-muted font-medium mb-1">{row.label}</div>
          <div className="space-y-0.5">
            {row.items.map(item => {
              const barWidth = Math.min(Math.max(item.pct, 2), 100);
              return (
                <div key={item.label} className="flex items-center gap-2">
                  <span className="text-micro text-secondary w-[5.5rem] shrink-0 truncate" title={item.label}>{item.label}</span>
                  <div className="flex-1 h-[14px] rounded-sm overflow-hidden" style={{ backgroundColor: 'var(--color-surface)' }}>
                    <div
                      className="h-full rounded-sm transition-all duration-300"
                      style={{ width: `${barWidth}%`, backgroundColor: `${segColor}30` }}
                    />
                  </div>
                  <span className="font-mono text-micro text-primary tabular-nums w-8 text-right shrink-0">{item.pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Attitude agreement bar — horizontal bar with percentage */
function AttitudeBar({ label, value, segColor }: { label: string; value: number; segColor: string }) {
  const barWidth = Math.min(Math.max(value, 2), 100);
  const colorClass = value >= 60 ? 'text-metric-positive' : value >= 40 ? 'text-primary' : 'text-metric-negative';

  return (
    <div className="flex items-center gap-2">
      <span className="text-micro text-secondary w-[5.5rem] shrink-0 truncate" title={label}>{label}</span>
      <div className="flex-1 h-[14px] rounded-sm overflow-hidden" style={{ backgroundColor: 'var(--color-surface)' }}>
        <div
          className="h-full rounded-sm transition-all duration-300"
          style={{ width: `${barWidth}%`, backgroundColor: value >= 60 ? `${segColor}40` : value >= 40 ? `${segColor}25` : 'rgba(220,38,38,0.15)' }}
        />
      </div>
      <span className={`font-mono text-micro font-medium tabular-nums w-8 text-right shrink-0 ${colorClass}`}>{value}%</span>
    </div>
  );
}

function shortenStatement(s: string): string {
  if (s.includes('DNA')) return 'DNA fitness';
  if (s.includes('Biometric')) return 'Biometric tracking';
  if (s.includes('Personalized')) return 'Personalized plans';
  if (s.includes('Recovery')) return 'Recovery content';
  if (s.includes('Nutrition')) return 'Nutrition guidance';
  return s.slice(0, 25);
}
